import { get } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import type { ComparisonSetVisibilityFlags } from 'lumen/components/hotel/benchmark-insight/tooltips/shared/tooltip-table';
import type { BeniBreakdown } from 'lumen/models';
import {
  type Kpi,
  KPI_LONG_TRANSLATIONS,
  KPIS,
  NUMBER_FORMATS,
  NUMBER_STYLES,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';

interface HotelBenchmarkInsightTooltipsSharedTooltipTableRowSignature {
  Args: {
    // The KPI the user has selected in the chart
    kpi: Kpi;
    ownComparisonSetBreakdown: BeniBreakdown | null;
    // The KPI for which we show data in this row
    ownComparisonSetVisibilityFlags: ComparisonSetVisibilityFlags;
    primaryComparisonSetBreakdown: BeniBreakdown | null;
    primaryComparisonSetVisibilityFlags: ComparisonSetVisibilityFlags;
    secondaryComparisonSetBreakdown: BeniBreakdown | null;
    secondaryComparisonSetVisibilityFlags: ComparisonSetVisibilityFlags;
    selectedKpi: Kpi;
    showMix: boolean;
    varianceTranslation?: string;
  };
}

export default class HotelBenchmarkInsightTooltipsSharedTooltipTableRow extends Component<HotelBenchmarkInsightTooltipsSharedTooltipTableRowSignature> {
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];

  get isOccupancyKpi(): boolean {
    return (
      this.args.kpi === KPIS.OCCUPANCY || this.args.kpi === KPIS.OCCUPANCY_MIX
    );
  }

  get isSelectedKpi(): boolean {
    if (
      this.args.kpi === KPIS.OCCUPANCY_MIX &&
      this.args.selectedKpi === KPIS.OCCUPANCY
    ) {
      return true;
    }
    return this.args.selectedKpi === this.args.kpi;
  }

  get ownCurrentPeriodValue(): number | null {
    const { ownComparisonSetBreakdown } = this.args;

    return this.getCurrentPeriodValueForComparisonSet(
      ownComparisonSetBreakdown,
    );
  }

  get ownCurrentPeriodValueFormatted(): string {
    return this.benchmarkInsightBeniService.formatNumber(
      this.ownCurrentPeriodValue,
      this.showPercentage ? NUMBER_FORMATS.PERCENTAGE : NUMBER_FORMATS.CURRENCY,
      NUMBER_STYLES.DEFAULT,
    );
  }

  get ownPickupValue(): number | null {
    const { ownComparisonSetBreakdown } = this.args;

    return this.getPickupValueForComparisonSet(ownComparisonSetBreakdown);
  }

  get ownPickupValueFormatted(): string {
    return this.benchmarkInsightBeniService.formatNumber(
      this.ownPickupValue,
      this.showPercentage ? NUMBER_FORMATS.PERCENTAGE : NUMBER_FORMATS.CURRENCY,
      NUMBER_STYLES.CHANGE,
    );
  }

  get ownReferencePeriodValue(): number | null {
    const { ownComparisonSetBreakdown } = this.args;

    return this.getReferencePeriodValueForComparisonSet(
      ownComparisonSetBreakdown,
    );
  }

  get ownReferencePeriodValueFormatted(): string {
    return this.benchmarkInsightBeniService.formatNumber(
      this.ownReferencePeriodValue,
      this.showPercentage ? NUMBER_FORMATS.PERCENTAGE : NUMBER_FORMATS.CURRENCY,
      NUMBER_STYLES.DEFAULT,
    );
  }

  get primaryCurrentPeriodValue(): number | null {
    const { primaryComparisonSetBreakdown } = this.args;

    return this.getCurrentPeriodValueForComparisonSet(
      primaryComparisonSetBreakdown,
    );
  }

  get primaryCurrentPeriodValueFormatted(): string {
    return this.benchmarkInsightBeniService.formatNumber(
      this.primaryCurrentPeriodValue,
      this.showPercentage ? NUMBER_FORMATS.PERCENTAGE : NUMBER_FORMATS.CURRENCY,
      NUMBER_STYLES.DEFAULT,
    );
  }

  get primaryPickupValue(): number | null {
    const { primaryComparisonSetBreakdown } = this.args;

    return this.getPickupValueForComparisonSet(primaryComparisonSetBreakdown);
  }

  get primaryPickupValueFormatted(): string {
    return this.benchmarkInsightBeniService.formatNumber(
      this.primaryPickupValue,
      this.showPercentage ? NUMBER_FORMATS.PERCENTAGE : NUMBER_FORMATS.CURRENCY,
      NUMBER_STYLES.CHANGE,
    );
  }

  get primaryReferencePeriodValue(): number | null {
    const { primaryComparisonSetBreakdown } = this.args;

    return this.getReferencePeriodValueForComparisonSet(
      primaryComparisonSetBreakdown,
    );
  }

  get primaryReferencePeriodValueFormatted(): string {
    return this.benchmarkInsightBeniService.formatNumber(
      this.primaryReferencePeriodValue,
      this.showPercentage ? NUMBER_FORMATS.PERCENTAGE : NUMBER_FORMATS.CURRENCY,
      NUMBER_STYLES.DEFAULT,
    );
  }

  get secondaryCurrentPeriodValue(): number | null {
    const { secondaryComparisonSetBreakdown } = this.args;

    return this.getCurrentPeriodValueForComparisonSet(
      secondaryComparisonSetBreakdown,
    );
  }

  get secondaryCurrentPeriodValueFormatted(): string {
    return this.benchmarkInsightBeniService.formatNumber(
      this.secondaryCurrentPeriodValue,
      this.showPercentage ? NUMBER_FORMATS.PERCENTAGE : NUMBER_FORMATS.CURRENCY,
      NUMBER_STYLES.DEFAULT,
    );
  }

  get secondaryReferencePeriodValue(): number | null {
    const { secondaryComparisonSetBreakdown } = this.args;

    return this.getReferencePeriodValueForComparisonSet(
      secondaryComparisonSetBreakdown,
    );
  }

  get secondaryReferencePeriodValueFormatted(): string {
    return this.benchmarkInsightBeniService.formatNumber(
      this.secondaryReferencePeriodValue,
      this.showPercentage ? NUMBER_FORMATS.PERCENTAGE : NUMBER_FORMATS.CURRENCY,
      NUMBER_STYLES.DEFAULT,
    );
  }

  get showPercentage(): boolean {
    return this.isOccupancyKpi;
  }

  getCurrentPeriodValueForComparisonSet(
    comparisonSetBreakdown: BeniBreakdown | null,
  ): number | null {
    if (!comparisonSetBreakdown) {
      return null;
    }

    return comparisonSetBreakdown.current.total[this.args.kpi] ?? null;
  }

  getPickupValueForComparisonSet(
    comparisonSetBreakdown: BeniBreakdown | null,
  ): number | null {
    if (!comparisonSetBreakdown) {
      return null;
    }

    return comparisonSetBreakdown.current.total.pickup[this.args.kpi] ?? null;
  }

  getReferencePeriodValueForComparisonSet(
    comparisonSetBreakdown: BeniBreakdown | null,
  ): number | null {
    if (!comparisonSetBreakdown) {
      return null;
    }

    return comparisonSetBreakdown.ref.total[this.args.kpi] ?? null;
  }

  <template>
    <tr
      class="tooltip-table__row
        {{if this.isSelectedKpi 'tooltip-table__row--highlighted' ''}}"
    >
      <td class="tooltip-table__row__cell">
        <div>
          {{t (get KPI_LONG_TRANSLATIONS @kpi)}}
        </div>
      </td>

      {{#if @ownComparisonSetVisibilityFlags.showComparisonSet}}
        <td
          class="tooltip-table__row__cell section-border value
            {{unless this.isSelectedKpi 'own'}}"
        >
          <div>
            {{this.ownCurrentPeriodValueFormatted}}
          </div>
        </td>

        {{#if @ownComparisonSetVisibilityFlags.showPickupColumn}}
          <td
            class="tooltip-table__row__cell value
              {{unless this.isSelectedKpi 'own'}}"
          >
            <div>
              {{this.ownPickupValueFormatted}}
            </div>
          </td>
        {{/if}}

        {{#if @ownComparisonSetVisibilityFlags.showReferencePeriodColumn}}
          <td
            class="tooltip-table__row__cell value
              {{unless this.isSelectedKpi 'own'}}"
          >
            <div>
              {{this.ownReferencePeriodValueFormatted}}
            </div>
          </td>
        {{/if}}
      {{/if}}

      {{#if @primaryComparisonSetVisibilityFlags.showComparisonSet}}
        <td class="tooltip-table__row__cell value section-border">
          <div>
            {{this.primaryCurrentPeriodValueFormatted}}
          </div>
        </td>
        {{#if @primaryComparisonSetVisibilityFlags.showPickupColumn}}
          <td class="tooltip-table__row__cell value">
            <div>
              {{this.primaryPickupValueFormatted}}
            </div>
          </td>
        {{/if}}
        {{#if @primaryComparisonSetVisibilityFlags.showReferencePeriodColumn}}
          <td class="tooltip-table__row__cell value">
            <div>
              {{this.primaryReferencePeriodValueFormatted}}
            </div>
          </td>
        {{/if}}
      {{/if}}

      {{#if @secondaryComparisonSetVisibilityFlags.showComparisonSet}}
        <td class="tooltip-table__row__cell value section-border">
          <div>
            {{this.secondaryCurrentPeriodValueFormatted}}
          </div>
        </td>
        {{#if @secondaryComparisonSetVisibilityFlags.showReferencePeriodColumn}}
          <td class="tooltip-table__row__cell value">
            <div>
              {{this.secondaryReferencePeriodValueFormatted}}
            </div>
          </td>
        {{/if}}
      {{/if}}
    </tr>
  </template>
}
